/**
 * Buddy List Component
 * Main links list window with AIM styling
 * File size: ~240 lines
 */

import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { WindowFrame } from './WindowFrame';
import { BuddyListItem } from './BuddyListItem';
import { Button98, PrimaryButton } from './Button98';
import { aimTheme } from '../../theme/aim-theme';
import type { Link, StatusType, BroadcastChannel as BroadcastChannelType } from '../../types';

interface BuddyListProps {
  links: Link[];
  channels?: BroadcastChannelType[];
  newMessageLinkIds?: Set<string>;
  onLinkClick: (linkId: string) => void;
  onLinkDoubleClick?: (linkId: string) => void;
  onLinkContextMenu?: (linkId: string, e: React.MouseEvent) => void;
  onChannelClick?: (channelId: string) => void;
  onChannelContextMenu?: (channelId: string, e: React.MouseEvent) => void;
  onCreateLink: () => void;
  onCreateChannel?: () => void;
  onSettings?: () => void;
  initialX?: number;
  initialY?: number;
  zIndex?: number;
  onFocus?: () => void;
}

const BuddyContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${aimTheme.colors.gray};
`;

const Header = styled.div`
  background: linear-gradient(to bottom, #FFFFFF, #E0E0E0);
  border-bottom: 1px solid ${aimTheme.colors.darkGray};
  padding: ${aimTheme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${aimTheme.spacing.md};
`;

const BrandLogo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3));
`;

const HeaderText = styled.div`
  flex: 1;
`;

const HeaderTitle = styled.div`
  font-weight: ${aimTheme.fonts.weight.bold};
  color: ${aimTheme.colors.brandOrange};
  font-size: ${aimTheme.fonts.size.medium};
`;

const HeaderSubtitle = styled.div`
  font-size: ${aimTheme.fonts.size.tiny};
  color: #666;
`;

const GroupHeader = styled.button`
  box-shadow: none;
  min-height: auto;
  min-width: auto;
  width: 100%;
  background: ${aimTheme.colors.gray};
  border: 1px solid ${aimTheme.colors.darkGray};
  border-left: none;
  border-right: none;
  padding: 2px 8px;
  font-family: ${aimTheme.fonts.primary};
  font-size: ${aimTheme.fonts.size.normal};
  font-weight: ${aimTheme.fonts.weight.bold};
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  text-align: left;

  &:hover {
    background: #D0D0D0;
  }
`;

const ScrollArea = styled.div`
  background: ${aimTheme.colors.white};
  box-shadow: var(--border-field);
  border: none;
  flex: 1;
  overflow-y: auto;
  margin: ${aimTheme.spacing.sm};
`;

const ActionButtons = styled.div`
  padding: ${aimTheme.spacing.sm};
  display: flex;
  gap: ${aimTheme.spacing.sm};
  border-top: 1px solid ${aimTheme.colors.darkGray};
  background: ${aimTheme.colors.gray};
`;


const getStatus = (link: Link): StatusType => {
  if (link.burned) return 'expired';
  if (!link.expires_at) return 'active';
  const expiresAt = new Date(link.expires_at);
  const now = new Date();
  const daysUntilExpiry = (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry < 3) return 'expiring';
  return 'active';
};

const getChannelStatus = (ch: BroadcastChannelType): StatusType => {
  if (ch.burned) return 'expired';
  return 'active';
};

export const BuddyList: React.FC<BuddyListProps> = ({
  links,
  channels = [],
  newMessageLinkIds,
  onLinkClick,
  onLinkDoubleClick,
  onLinkContextMenu,
  onChannelClick,
  onChannelContextMenu,
  onCreateLink,
  onCreateChannel,
  onSettings,
  initialX = 50,
  initialY = 50,
  zIndex = 100,
  onFocus,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['active', 'broadcast']));

  const handleLinkClick = useCallback((linkId: string) => {
    onLinkClick(linkId);
  }, [onLinkClick]);

  const handleChannelClick = useCallback((channelId: string) => {
    onChannelClick?.(channelId);
  }, [onChannelClick]);

  const activeLinks = links.filter((l) => getStatus(l) === 'active');
  const expiringLinks = links.filter((l) => getStatus(l) === 'expiring');
  const expiredLinks = links.filter((l) => getStatus(l) === 'expired');

  const activeChannels = channels.filter((c) => !c.burned);
  const expiredChannels = channels.filter((c) => c.burned);

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <WindowFrame
      title="🔥 BurnWare - My Anonymous Links"
      width={260}
      height={450}
      initialX={initialX}
      initialY={initialY}
      zIndex={zIndex}
      onFocus={onFocus}
    >
      <BuddyContainer>
        <Header>
          <BrandLogo src="/burnware-logo.png" alt="BurnWare" />
          <HeaderText>
            <HeaderTitle>BurnWare</HeaderTitle>
            <HeaderSubtitle>{activeLinks.length + expiringLinks.length} active</HeaderSubtitle>
          </HeaderText>
        </Header>

        <ScrollArea>
          <GroupHeader
            onClick={() => toggleGroup('active')}
            aria-expanded={expandedGroups.has('active')}
            aria-controls="group-active"
          >
            <span>{expandedGroups.has('active') ? '▼' : '▶'}</span>
            <span>Active Links ({activeLinks.length})</span>
          </GroupHeader>
          {expandedGroups.has('active') &&
            activeLinks.map((link) => (
              <BuddyListItem
                key={link.link_id}
                name={link.display_name}
                status="active"
                messageCount={link.message_count}
                hasNewMessages={newMessageLinkIds?.has(link.link_id)}
                onClick={() => handleLinkClick(link.link_id)}
                onDoubleClick={onLinkDoubleClick ? () => onLinkDoubleClick(link.link_id) : undefined}
                onContextMenu={onLinkContextMenu ? (e) => onLinkContextMenu(link.link_id, e) : undefined}
              />
            ))}

          {expiringLinks.length > 0 && (
            <>
              <GroupHeader
                onClick={() => toggleGroup('expiring')}
                aria-expanded={expandedGroups.has('expiring')}
                aria-controls="group-expiring"
              >
                <span>{expandedGroups.has('expiring') ? '▼' : '▶'}</span>
                <span>Expiring Soon ({expiringLinks.length})</span>
              </GroupHeader>
              {expandedGroups.has('expiring') &&
                expiringLinks.map((link) => (
                  <BuddyListItem
                    key={link.link_id}
                    name={link.display_name}
                    status="expiring"
                    messageCount={link.message_count}
                    hasNewMessages={newMessageLinkIds?.has(link.link_id)}
                    onClick={() => handleLinkClick(link.link_id)}
                    onDoubleClick={onLinkDoubleClick ? () => onLinkDoubleClick(link.link_id) : undefined}
                    onContextMenu={onLinkContextMenu ? (e) => onLinkContextMenu(link.link_id, e) : undefined}
                  />
                ))}
            </>
          )}

          <GroupHeader
            onClick={() => toggleGroup('expired')}
            aria-expanded={expandedGroups.has('expired')}
            aria-controls="group-expired"
          >
            <span>{expandedGroups.has('expired') ? '▼' : '▶'}</span>
            <span>Expired ({expiredLinks.length})</span>
          </GroupHeader>
          {expandedGroups.has('expired') &&
            expiredLinks.map((link) => (
              <BuddyListItem
                key={link.link_id}
                name={link.display_name}
                status="expired"
                messageCount={0}
                onClick={() => handleLinkClick(link.link_id)}
                onDoubleClick={onLinkDoubleClick ? () => onLinkDoubleClick(link.link_id) : undefined}
                onContextMenu={onLinkContextMenu ? (e) => onLinkContextMenu(link.link_id, e) : undefined}
              />
            ))}

          {/* Broadcast Section */}
          <GroupHeader
            onClick={() => toggleGroup('broadcast')}
            aria-expanded={expandedGroups.has('broadcast')}
            aria-controls="group-broadcast"
            style={{ marginTop: '8px', borderTop: `2px solid ${aimTheme.colors.darkGray}` }}
          >
            <span>{expandedGroups.has('broadcast') ? '▼' : '▶'}</span>
            <span>📡 Broadcast ({activeChannels.length})</span>
          </GroupHeader>
          {expandedGroups.has('broadcast') && (
            <>
              {activeChannels.map((ch) => (
                <BuddyListItem
                  key={ch.channel_id}
                  name={ch.display_name}
                  status={getChannelStatus(ch)}
                  messageCount={0}
                  isRoom
                  onClick={() => handleChannelClick(ch.channel_id)}
                  onContextMenu={onChannelContextMenu ? (e) => onChannelContextMenu(ch.channel_id, e) : undefined}
                />
              ))}
              {onCreateChannel && activeChannels.length === 0 && (
                <div style={{ padding: '8px', fontSize: aimTheme.fonts.size.small, color: aimTheme.colors.darkGray, textAlign: 'center' }}>
                  No channels yet
                </div>
              )}
            </>
          )}

          {expiredChannels.length > 0 && (
            <>
              <GroupHeader
                onClick={() => toggleGroup('expiredChannels')}
                aria-expanded={expandedGroups.has('expiredChannels')}
                aria-controls="group-expired-channels"
              >
                <span>{expandedGroups.has('expiredChannels') ? '▼' : '▶'}</span>
                <span>Burned channels ({expiredChannels.length})</span>
              </GroupHeader>
              {expandedGroups.has('expiredChannels') &&
                expiredChannels.map((ch) => (
                  <BuddyListItem
                    key={ch.channel_id}
                    name={ch.display_name}
                    status="expired"
                    messageCount={0}
                    isRoom
                    onClick={() => handleChannelClick(ch.channel_id)}
                    onContextMenu={onChannelContextMenu ? (e) => onChannelContextMenu(ch.channel_id, e) : undefined}
                  />
                ))}
            </>
          )}
        </ScrollArea>

        <ActionButtons>
          <PrimaryButton onClick={onCreateLink} style={{ flex: 1 }}>✨ New Link</PrimaryButton>
          {onCreateChannel && <Button98 onClick={onCreateChannel} style={{ flex: 1 }}>📡 New channel</Button98>}
          {onSettings && <Button98 onClick={onSettings} style={{ flex: 1 }}>Settings</Button98>}
        </ActionButtons>
      </BuddyContainer>
    </WindowFrame>
  );
};
